import React, { useEffect, useState } from "react";
import { Card } from "../../../components/card/card.jsx";
import { apiUrl } from "../../../config/config";
import ReactPaginate from "react-paginate";
import "../../../components/table/TableBody.css";
import { alerts } from "../../../config/sweetalert-config";
import Chart from "react-apexcharts";
import moment from "moment";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";

function Reports() {
	const [isLoading, setIsLoading] = useState(false);
	const [data, setData] = useState([]);
	const [originalData, setOriginalData] = useState([]);
	const [apiTotals, setApiTotals] = useState({});
	const [selectedTable, setSelectedTable] = useState("accounts");
	const [chartData, setChartData] = useState(null);
	const [stats, setStats] = useState({
		total: 0,
		active: 0,
		inactive: 0,
		average: 0,
	});

	// Estados para filtros
	const [dateRange, setDateRange] = useState({
		startDate: moment().subtract(30, "days"),
		endDate: moment(),
	});
	const [filterStatus, setFilterStatus] = useState("all");
	const [filterSymbol, setFilterSymbol] = useState("all");
	const [filterAccountType, setFilterAccountType] = useState("all");
	const [filterPositionStatus, setFilterPositionStatus] = useState("all");
	const [filterMasterName, setFilterMasterName] = useState("all");

	// Estados para la paginación
	const [currentPage, setCurrentPage] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(20);
	const [searchTerm, setSearchTerm] = useState("");

	// Tablas disponibles para reportes
	const availableTables = [
		{ value: "accounts", label: "Cuentas", icon: "fa-briefcase" },
		{ value: "copyTrading", label: "Copy Trading", icon: "fa-copy" },
		{ value: "positionsHistory", label: "Historial de Posiciones", icon: "fa-history" },
		{ value: "users", label: "Usuarios", icon: "fa-users" },
		{ value: "tickets", label: "Tickets de Soporte", icon: "fa-ticket" },
		{ value: "markup", label: "Markup", icon: "fa-chart-area" },
	];

	useEffect(() => {
		// Reset filters and chart when changing tables
		setFilterSymbol("all");
		setFilterAccountType("all");
		setFilterPositionStatus("all");
		setFilterMasterName("all");
		setChartData(null); // Limpiar gráfico anterior
		setData([]); // Limpiar datos anteriores
		loadTableData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedTable]);

	useEffect(() => {
		if (data.length > 0) {
			generateChartData();
			calculateStats();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	const loadTableDataWithDates = async (customDateRange = null) => {
		const effectiveDateRange = customDateRange || dateRange;
		setIsLoading(true);
		setData([]);
		try {
			const userProfile = JSON.parse(localStorage.getItem("userProfile") || "{}");
			let endpoint = "/getList";
			let body = {
				table: selectedTable,
				...(selectedTable === "ibUsers" || selectedTable === "ibPurchases" ? { userId: userProfile.id } : {}),
			};

			if (selectedTable === "markup") {
				endpoint = "/getAppMarkupStatistics";
				body = {
					userId: userProfile.id,
					startDate: effectiveDateRange.startDate.format("YYYY-MM-DD"),
					endDate: effectiveDateRange.endDate.format("YYYY-MM-DD"),
				};
			} else if (selectedTable === "copyTrading") {
				body = {
					table: selectedTable,
					startDate: effectiveDateRange.startDate.format("YYYY-MM-DD"),
					endDate: effectiveDateRange.endDate.format("YYYY-MM-DD"),
					...(selectedTable === "ibUsers" || selectedTable === "ibPurchases" ? { userId: userProfile.id } : {}),
				};
			}

			const response = await fetch(apiUrl + endpoint, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(body),
			});

			if (response.ok) {
				let responseData = await response.json();
				if (selectedTable === "markup") {
					console.log("Markup API Response:", responseData);
				}
				if (selectedTable === "copyTrading") {
					console.log("CopyTrading API Response:", responseData);
				}

				if (responseData.ok && responseData.data) {
					// Store totals if available
					setApiTotals(responseData.totals || {});

					if (!Array.isArray(responseData.data)) {
						responseData = [
							{
								...responseData.data,
								label: selectedTable === "tournaments" ? "Torneos" : "Totales", // Etiqueta para el gráfico
								category: "Total", // Categoría para identificación
							},
						];
					} else {
						responseData = responseData.data;
					}
				} else if (!Array.isArray(responseData)) {
					if (responseData.data && Array.isArray(responseData.data)) {
						responseData = responseData.data;
					} else if (responseData.result && Array.isArray(responseData.result)) {
						responseData = responseData.result;
					} else if (responseData.rows && Array.isArray(responseData.rows)) {
						responseData = responseData.rows;
					} else {
						responseData = [];
					}
				}

				setData(responseData);
				setOriginalData(responseData);
			} else {
				console.error("❌ Error en respuesta:", response.status);
				alerts.error("Error", "No se pudieron cargar los datos");
			}
		} catch (error) {
			console.error("❌ Error al cargar datos:", error);
			alerts.error("Error de conexión", "No se pudo conectar con el servidor");
		} finally {
			setIsLoading(false);
		}
	};

	const loadTableData = async () => {
		await loadTableDataWithDates();
	};

	const generateChartData = () => {
		if (!data || data.length === 0) return;

		// Generar datos según la tabla seleccionada
		let chartConfig = null;

		try {
			switch (selectedTable) {
				case "accounts":
					chartConfig = generateAccountsChart();
					break;
				case "copyTrading":
					chartConfig = generateCopyTradingChart();
					break;
				case "positionsHistory":
					chartConfig = generatePositionsChart();
					break;
				case "users":
					chartConfig = generateUsersChart();
					break;
				case "tickets":
					chartConfig = generateTicketsChart();
					break;
				case "markup":
					chartConfig = generateMarkupChart();
					break;
				default:
					chartConfig = generateDefaultChart();
			}
		} catch (error) {
			console.error(`Error generando gráfico para ${selectedTable}:`, error);
			chartConfig = generateDefaultChart();
		}

		setChartData(chartConfig);
	};

	const generateAccountsChart = () => {
		try {
			// Análisis específico para accounts
			const accountsByUser = {};
			const realVsDemo = { "Cuentas Reales": 0, "Cuentas Demo": 0 };
			const usersWithAccounts = { "Con Cuenta Real": 0, "Con Cuenta Demo": 0, "Sin Cuentas": 0 };

			data.forEach((account) => {
				const userId = account.userId;

				// Contar cuentas por usuario
				if (!accountsByUser[userId]) {
					accountsByUser[userId] = { real: false, demo: false };
				}

				// Verificar si tiene cuentas reales o demo
				if (account.realAcc && account.realAcc.trim() !== "") {
					realVsDemo["Cuentas Reales"]++;
					accountsByUser[userId].real = true;
				}

				if (account.demoAcc && account.demoAcc.trim() !== "") {
					realVsDemo["Cuentas Demo"]++;
					accountsByUser[userId].demo = true;
				}
			});

			// Clasificar usuarios según sus cuentas
			Object.values(accountsByUser).forEach((user) => {
				if (user.real && user.demo) {
					usersWithAccounts["Con Cuenta Real"]++;
				} else if (user.real) {
					usersWithAccounts["Con Cuenta Real"]++;
				} else if (user.demo) {
					usersWithAccounts["Con Cuenta Demo"]++;
				} else {
					usersWithAccounts["Sin Cuentas"]++;
				}
			});

			// Gráfico principal: Real vs Demo
			if (realVsDemo["Cuentas Reales"] > 0 || realVsDemo["Cuentas Demo"] > 0) {
				return {
					series: [
						{
							name: "Tipo de Cuenta",
							data: [
								{ x: "Reales", y: realVsDemo["Cuentas Reales"] },
								{ x: "Demo", y: realVsDemo["Cuentas Demo"] },
							],
						},
					],
					options: {
						chart: {
							type: "bar",
							height: 350,
						},
						plotOptions: {
							bar: {
								borderRadius: 4,
								distributed: true,
								columnWidth: "50%",
							},
						},
						colors: ["#00E396", "#008FFB"],
						title: {
							text: "Distribución de Cuentas: Real vs Demo",
							align: "center",
						},
						xaxis: {
							categories: ["Cuentas Reales", "Cuentas Demo"],
						},
						dataLabels: {
							enabled: true,
						},
						tooltip: {
							y: {
								formatter: function (val) {
									return (val || 0) + " cuentas";
								},
							},
						},
					},
				};
			} else {
				return generateDefaultChart();
			}
		} catch (error) {
			console.error("Error generando gráfico de cuentas:", error);
			return generateDefaultChart();
		}
	};

	const generateCopyTradingChart = () => {
		try {
			if (!data || data.length === 0) {
				return generateDefaultChart();
			}

			// Análisis específico para copyTrading
			const copyByDate = {};
			const copyAmounts = { "Pequeño ($1-$50)": 0, "Medio ($51-$200)": 0, "Grande ($200+)": 0 };
			const masterCount = {};

			data.forEach((copy) => {
				// Agrupar por fecha de creación
				if (copy.createdAt && moment(copy.createdAt).isValid()) {
					const date = moment(copy.createdAt).format("YYYY-MM-DD");
					copyByDate[date] = (copyByDate[date] || 0) + 1;
				}

				// Agrupar por cantidad de copy
				const amount = parseFloat(copy.copyAmount) || 0;
				if (amount > 0 && amount <= 50) {
					copyAmounts["Pequeño ($1-$50)"]++;
				} else if (amount > 50 && amount <= 200) {
					copyAmounts["Medio ($51-$200)"]++;
				} else if (amount > 200) {
					copyAmounts["Grande ($200+)"]++;
				}

				// Contar por master trader
				const masterName = copy.masterName || "Sin Master";
				masterCount[masterName] = (masterCount[masterName] || 0) + 1;
			});

			const dates = Object.keys(copyByDate).sort().slice(-30);

			// Gráfico principal: Evolución de copias por fecha
			if (dates.length > 0) {
				return {
					series: [
						{
							name: "Nuevas Configuraciones Copy Trading",
							data: dates.map((date) => copyByDate[date] || 0),
						},
					],
					options: {
						chart: {
							type: "line",
							height: 350,
						},
						stroke: {
							curve: "smooth",
							width: 3,
						},
						markers: {
							size: 5,
						},
						title: {
							text: "Evolución de Configuraciones Copy Trading",
							align: "center",
						},
						xaxis: {
							categories: dates.map((d) => moment(d).format("DD/MM")),
							labels: {
								rotate: -45,
							},
						},
						colors: ["#008FFB"],
						tooltip: {
							y: {
								formatter: function (val) {
									return (val || 0) + " configuraciones";
								},
							},
						},
					},
				};
			}
			// Si no hay fechas, mostrar distribución por Master Traders
			else if (Object.keys(masterCount).length > 0) {
				const topMasters = Object.entries(masterCount)
					.filter(([name, count]) => name && typeof count === 'number')
					.sort((a, b) => b[1] - a[1])
					.slice(0, 10);

				if (topMasters.length === 0) {
					return generateDefaultChart();
				}

				return {
					series: [
						{
							name: "Followers",
							data: topMasters.map(([, count]) => Number(count) || 0),
						},
					],
					options: {
						chart: {
							type: "bar",
							height: 350,
							width: "100%",
						},
						plotOptions: {
							bar: {
								borderRadius: 4,
								horizontal: true,
							},
						},
						dataLabels: {
							enabled: true,
						},
						xaxis: {
							categories: topMasters.map(([name]) => String(name) || "N/A"),
						},
						title: {
							text: "Top Master Traders por Followers",
							align: "center",
						},
						colors: ["#008FFB"],
						tooltip: {
							y: {
								formatter: function (val) {
									return (val || 0) + " followers";
								},
							},
						},
					},
				};
			}
			// Si tampoco hay masters, mostrar distribución por montos
			else if (Object.values(copyAmounts).some((val) => val > 0)) {
				return {
					series: Object.values(copyAmounts).map(v => Number(v) || 0),
					options: {
						chart: {
							type: "donut",
							height: 350,
							width: "100%",
						},
						labels: Object.keys(copyAmounts),
						title: {
							text: "Distribución por Monto de Copy Trading",
							align: "center",
						},
						colors: ["#00E396", "#FEB019", "#FF4560"],
						legend: {
							position: "bottom",
						},
					},
				};
			} else {
				return generateDefaultChart();
			}
		} catch (error) {
			console.error("Error generando gráfico de copy trading:", error);
			return generateDefaultChart();
		}
	};

	const generatePositionsChart = () => {
		try {
			// Análisis específico para positionsHistory
			const positionsByDate = {};
			const symbolsCount = {};
			const contractTypes = {};
			const profitLossData = [];

			data.forEach((position) => {
				// Fechas de entrada y cierre
				const entryDate = moment(position.entryDate).format("YYYY-MM-DD");

				if (!positionsByDate[entryDate]) {
					positionsByDate[entryDate] = {
						profit: 0,
						loss: 0,
						count: 0,
					};
				}

				// Calcular P&L basado en balances
				const entryBalance = parseFloat(position.entryBalance || 0);
				const closeBalance = parseFloat(position.closeBalance || 0);
				const pnl = closeBalance - entryBalance;

				if (pnl > 0) {
					positionsByDate[entryDate].profit += pnl;
				} else {
					positionsByDate[entryDate].loss += Math.abs(pnl);
				}
				positionsByDate[entryDate].count++;

				// Contar símbolos
				const symbol = position.symbol || "Unknown";
				symbolsCount[symbol] = (symbolsCount[symbol] || 0) + 1;

				// Tipos de contrato
				const contractType = position.contractType || "Unknown";
				contractTypes[contractType] = (contractTypes[contractType] || 0) + 1;

				// Datos para gráfico de dispersión
				profitLossData.push({
					x: parseFloat(position.amount || 0),
					y: pnl,
					symbol: symbol,
					type: position.type,
				});
			});

			const dates = Object.keys(positionsByDate).sort().slice(-30);

			// Gráfico principal: P&L por fecha
			if (dates.length > 0) {
				return {
					series: [
						{
							name: "Ganancias",
							data: dates.map((date) => parseFloat(positionsByDate[date].profit.toFixed(2))),
						},
						{
							name: "Pérdidas",
							data: dates.map((date) => parseFloat(positionsByDate[date].loss.toFixed(2))),
						},
						{
							name: "Operaciones",
							data: dates.map((date) => positionsByDate[date].count),
							type: "line",
						},
					],
					options: {
						chart: {
							type: "area",
							height: 350,
							stacked: false,
						},
						colors: ["#00E396", "#FF4560", "#008FFB"],
						dataLabels: {
							enabled: false,
						},
						stroke: {
							curve: "smooth",
							width: [2, 2, 3],
						},
						title: {
							text: "P&L y Volumen de Operaciones por Fecha",
							align: "center",
						},
						xaxis: {
							type: "category",
							categories: dates.map((d) => moment(d).format("DD/MM")),
							labels: {
								rotate: -45,
							},
						},
						yaxis: [
							{
								title: {
									text: "P&L ($)",
								},
							},
							{
								opposite: true,
								title: {
									text: "Número de Operaciones",
								},
							},
						],
						tooltip: {
							shared: true,
							intersect: false,
							y: [
								{
									formatter: function (val) {
										return "$" + (parseFloat(val) || 0).toFixed(2);
									},
								},
								{
									formatter: function (val) {
										return "$" + (parseFloat(val) || 0).toFixed(2);
									},
								},
								{
									formatter: function (val) {
										return (parseInt(val) || 0) + " ops";
									},
								},
							],
						},
					},
				};
			}
			// Si no hay fechas, mostrar distribución por símbolo
			else if (Object.keys(symbolsCount).length > 0) {
				return {
					series: Object.values(symbolsCount),
					options: {
						chart: {
							type: "donut",
							height: 350,
						},
						labels: Object.keys(symbolsCount),
						title: {
							text: "Distribución de Operaciones por Símbolo",
							align: "center",
						},
						colors: ["#00E396", "#008FFB", "#FEB019", "#FF4560", "#775DD0"],
					},
				};
			} else {
				return generateDefaultChart();
			}
		} catch (error) {
			console.error("Error generando gráfico de posiciones:", error);
			return generateDefaultChart();
		}
	};

	const generateUsersChart = () => {
		try {
			// Análisis específico para users
			const rolesCount = {};
			const statusCount = { Activos: 0, Inactivos: 0, Eliminados: 0 };
			const featuresCount = {
				"Con Imagen de Perfil": 0,
				"Con Copy Trading": 0,
				"Con Telegram": 0,
				"Con Instagram": 0,
				"Con Opciones Pagadas": 0,
			};

			data.forEach((user) => {
				// Contar por rol
				const role = user.role || "Usuario";
				rolesCount[role] = (rolesCount[role] || 0) + 1;

				// Estado del usuario
				if (user.deleted === "1") {
					statusCount["Eliminados"]++;
				} else if (user.status === null || user.status === "1") {
					statusCount["Activos"]++;
				} else {
					statusCount["Inactivos"]++;
				}

				// Características del usuario
				if (user.profileImage) featuresCount["Con Imagen de Perfil"]++;
				if (user.copyTrader) featuresCount["Con Copy Trading"]++;
				if (user.telegram) featuresCount["Con Telegram"]++;
				if (user.instagram) featuresCount["Con Instagram"]++;
				if (user.paidOptions) featuresCount["Con Opciones Pagadas"]++;
			});

			// Gráfico principal: Distribución por roles
			if (Object.keys(rolesCount).length > 1) {
				return {
					series: Object.values(rolesCount),
					options: {
						chart: {
							type: "pie",
							height: 350,
						},
						labels: Object.keys(rolesCount).map((role) =>
							role === "admin" ? "Administradores" : role === "customer" ? "Clientes" : role.charAt(0).toUpperCase() + role.slice(1)
						),
						title: {
							text: "Distribución de Usuarios por Rol",
							align: "center",
						},
						colors: ["#FF4560", "#008FFB", "#00E396", "#FEB019", "#775DD0"],
						legend: {
							position: "bottom",
						},
						dataLabels: {
							enabled: true,
							formatter: function (val, opts) {
								if (!opts || !opts.w || !opts.w.config || !opts.w.config.labels || opts.seriesIndex === undefined) {
									return Math.round(val) + "%";
								}
								return opts.w.config.labels[opts.seriesIndex] + ": " + Math.round(val) + "%";
							},
						},
					},
				};
			}
			// Si no hay variedad de roles, mostrar estado
			else {
				return {
					series: [
						{
							name: "Estado de Usuarios",
							data: [
								{ x: "Activos", y: statusCount["Activos"] },
								{ x: "Eliminados", y: statusCount["Eliminados"] },
							],
						},
					],
					options: {
						chart: {
							type: "bar",
							height: 350,
						},
						plotOptions: {
							bar: {
								borderRadius: 4,
								distributed: true,
							},
						},
						colors: ["#00E396", "#FF4560"],
						title: {
							text: "Estado de Usuarios",
							align: "center",
						},
						dataLabels: {
							enabled: true,
						},
					},
				};
			}
		} catch (error) {
			console.error("Error generando gráfico de usuarios:", error);
			return generateDefaultChart();
		}
	};

	const generateTicketsChart = () => {
		try {
			// Análisis específico para tickets
			const ticketsByDate = {};
			const statusCount = {};
			const typeCount = {};
			const responseTimeData = [];

			data.forEach((ticket) => {
				// Agrupar por fecha de creación
				if (ticket.createdAt) {
					const date = moment(ticket.createdAt).format("YYYY-MM-DD");
					ticketsByDate[date] = (ticketsByDate[date] || 0) + 1;
				}

				// Estado de tickets
				const status = ticket.status || "open";
				const statusLabel = status === "closed" ? "Cerrado" : status === "open" ? "Abierto" : status === "pending" ? "Pendiente" : "Otros";
				statusCount[statusLabel] = (statusCount[statusLabel] || 0) + 1;

				// Tipo de ticket
				const type = ticket.type || "General";
				typeCount[type] = (typeCount[type] || 0) + 1;

				// Tiempo de respuesta (si está cerrado)
				if (ticket.status === "closed" && ticket.createdAt && ticket.updatedAt) {
					const created = moment(ticket.createdAt);
					const updated = moment(ticket.updatedAt);
					const hours = updated.diff(created, "hours");
					responseTimeData.push(hours);
				}
			});

			const dates = Object.keys(ticketsByDate).sort().slice(-30);

			// Gráfico principal: Tickets por fecha
			if (dates.length > 0) {
				return {
					series: [
						{
							name: "Nuevos Tickets",
							data: dates.map((date) => ticketsByDate[date]),
						},
					],
					options: {
						chart: {
							type: "area",
							height: 350,
						},
						colors: ["#008FFB"],
						fill: {
							type: "gradient",
							gradient: {
								shadeIntensity: 1,
								opacityFrom: 0.7,
								opacityTo: 0.3,
								stops: [0, 90, 100],
							},
						},
						dataLabels: {
							enabled: false,
						},
						stroke: {
							curve: "smooth",
						},
						title: {
							text: "Tickets de Soporte por Fecha",
							align: "center",
						},
						xaxis: {
							categories: dates.map((d) => moment(d).format("DD/MM")),
							labels: {
								rotate: -45,
							},
						},
						tooltip: {
							y: {
								formatter: function (val) {
									return (val || 0) + " tickets";
								},
							},
						},
					},
				};
			}
			// Si no hay fechas, mostrar estado de tickets
			else if (Object.keys(statusCount).length > 0) {
				return {
					series: Object.values(statusCount),
					options: {
						chart: {
							type: "pie",
							height: 350,
						},
						labels: Object.keys(statusCount),
						title: {
							text: "Estado Actual de Tickets",
							align: "center",
						},
						colors: ["#00E396", "#FEB019", "#FF4560", "#775DD0"],
						legend: {
							position: "bottom",
						},
						dataLabels: {
							enabled: true,
							formatter: function (val, opts) {
								if (!opts || !opts.w || !opts.w.config || !opts.w.config.labels || opts.seriesIndex === undefined) {
									return Math.round(val) + "%";
								}
								return opts.w.config.labels[opts.seriesIndex] + "\n" + Math.round(val) + "%";
							},
						},
					},
				};
			} else {
				return generateDefaultChart();
			}
		} catch (error) {
			console.error("Error generando gráfico de tickets:", error);
			return generateDefaultChart();
		}
	};

	const generateMarkupChart = () => {
		try {
			if (!data || data.length === 0) {
				return generateDefaultChart();
			}

			// Extraer datos específicos de markup
			const markupData = data[0]; // Tomar el primer objeto del array
			const markupUsd = parseFloat(markupData.app_markup_usd || 0);
			const markupValue = parseFloat(markupData.app_markup_value || 0);
			const transactionsCount = parseInt(markupData.transactions_count || 0);
			const appId = markupData.app_id || "N/A";

			// Crear gráfico de barras con las métricas principales
			return {
				series: [
					{
						name: "Markup USD",
						data: [markupUsd],
					},
					{
						name: "Markup Value",
						data: [markupValue],
					},
					{
						name: "Transactions Count",
						data: [transactionsCount],
					},
				],
				options: {
					chart: {
						type: "bar",
						height: 350,
						stacked: false,
					},
					plotOptions: {
						bar: {
							horizontal: false,
							columnWidth: "55%",
							endingShape: "rounded",
							distributed: false,
						},
					},
					dataLabels: {
						enabled: true,
						formatter: function (val, opts) {
							// Verificar que opts existe
							if (!opts || opts.seriesIndex === undefined || val === undefined || val === null) {
								return val ? val.toString() : "0";
							}
							// Formatear según la serie
							if (opts.seriesIndex === 2) {
								// Transactions Count
								return val.toString();
							} else {
								// Markup USD/Value
								return "$" + (parseFloat(val) || 0).toFixed(2);
							}
						},
					},
					title: {
						text: `Análisis de Markup - App ${appId}`,
						align: "center",
					},
					xaxis: {
						categories: ["Métricas de Markup"],
						labels: {
							show: false,
						},
					},
					yaxis: [
						{
							title: {
								text: "Valor USD ($)",
							},
							labels: {
								formatter: function (val) {
									return "$" + (parseFloat(val) || 0).toFixed(2);
								},
							},
						},
						{
							opposite: true,
							title: {
								text: "Número de Transacciones",
							},
							labels: {
								formatter: function (val) {
									return (val || 0).toString();
								},
							},
						},
					],
					colors: ["#8e44ad", "#3498db", "#e74c3c"],
					legend: {
						position: "top",
						horizontalAlign: "center",
					},
					tooltip: {
						shared: true,
						intersect: false,
						y: [
							{
								formatter: function (val) {
									return "$" + (parseFloat(val) || 0).toFixed(2) + " USD";
								},
							},
							{
								formatter: function (val) {
									return "$" + (parseFloat(val) || 0).toFixed(2);
								},
							},
							{
								formatter: function (val) {
									return (val || 0) + " transacciones";
								},
							},
						],
					},
					responsive: [
						{
							breakpoint: 480,
							options: {
								legend: {
									position: "bottom",
									offsetX: -10,
									offsetY: 0,
								},
							},
						},
					],
				},
			};
		} catch (error) {
			console.error("Error generando gráfico de markup:", error);
			return generateDefaultChart();
		}
	};

	const generateDefaultChart = () => {
		// Gráfico genérico para datos no específicos
		const total = data?.length || 0;
		return {
			series: [
				{
					name: "Registros",
					data: [total],
				},
			],
			options: {
				chart: {
					type: "bar",
					height: 350,
					width: "100%",
				},
				plotOptions: {
					bar: {
						borderRadius: 4,
						columnWidth: "50%",
					},
				},
				dataLabels: {
					enabled: true,
				},
				xaxis: {
					categories: ["Total"],
				},
				title: {
					text: `Total de ${selectedTable}`,
					align: "center",
				},
				colors: ["#008FFB"],
			},
		};
	};

	const calculateStatsForData = (dataToAnalyze) => {
		if (!dataToAnalyze || dataToAnalyze.length === 0) {
			setStats({
				total: 0,
				active: 0,
				inactive: 0,
				average: 0,
			});
			return;
		}

		let newStats = {
			total: dataToAnalyze.length,
			active: 0,
			inactive: 0,
			average: 0,
		};

		// Calcular estadísticas según la tabla
		switch (selectedTable) {
			case "accounts":
				newStats.active = dataToAnalyze.filter((d) => d.realAcc && d.realAcc.trim() !== "").length;
				newStats.inactive = dataToAnalyze.filter((d) => d.demoAcc && d.demoAcc.trim() !== "").length;
				newStats.average = 0; // No hay balance en esta estructura
				break;

			case "positionsHistory":
				const profitPositions = dataToAnalyze.filter((d) => {
					const entryBalance = parseFloat(d.entryBalance || 0);
					const closeBalance = parseFloat(d.closeBalance || 0);
					return closeBalance > entryBalance;
				}).length;
				newStats.active = profitPositions;
				newStats.inactive = dataToAnalyze.length - profitPositions;

				// Suma total de amounts
				const totalAmount = dataToAnalyze.reduce((sum, pos) => {
					const amount = parseFloat(pos.amount || 0);
					return sum + amount;
				}, 0);
				newStats.average = totalAmount.toFixed(2);
				break;

			case "users":
				newStats.active = dataToAnalyze.filter((d) => d.deleted !== "1").length;
				newStats.inactive = dataToAnalyze.filter((d) => d.deleted === "1").length;
				// Promedio de usuarios con rol admin
				const admins = dataToAnalyze.filter((d) => d.role === "admin").length;
				newStats.average = ((admins / dataToAnalyze.length) * 100).toFixed(1) + "%";
				break;

			case "tickets":
				newStats.active = dataToAnalyze.filter((d) => d.status === "open").length;
				newStats.inactive = dataToAnalyze.filter((d) => d.status === "closed").length;

				// Tiempo promedio de resolución
				const closedTickets = dataToAnalyze.filter((d) => d.status === "closed" && d.createdAt && d.updatedAt);
				if (closedTickets.length > 0) {
					const totalHours = closedTickets.reduce((sum, ticket) => {
						const created = moment(ticket.createdAt);
						const updated = moment(ticket.updatedAt);
						return sum + updated.diff(created, "hours");
					}, 0);
					newStats.average = (totalHours / closedTickets.length).toFixed(1) + "h";
				}
				break;

			case "copyTrading":
				// Use totals from API response if available, but for filtered data use actual counts
				if (dataToAnalyze.length === originalData.length) {
					// No filtering applied, use API totals
					newStats.active = apiTotals.uniqueMasters || 0;
					newStats.inactive = apiTotals.totalFollowers || dataToAnalyze.length;
					newStats.average = "$" + (apiTotals.grandTotalCopyAmount || 0).toFixed(2);
				} else {
					// Filtering applied, calculate from filtered data
					const uniqueMasters = [...new Set(dataToAnalyze.map((d) => d.masterUserId).filter(Boolean))].length;
					const totalAmount = dataToAnalyze.reduce((sum, d) => sum + parseFloat(d.copyAmount || 0), 0);
					newStats.active = uniqueMasters;
					newStats.inactive = dataToAnalyze.length;
					newStats.average = "$" + totalAmount.toFixed(2);
				}
				break;

			case "markup":
				// Estadísticas temporales para markup - se ajustarán con datos reales
				newStats.active = dataToAnalyze.length; // Se ajustará según la lógica de markup
				newStats.inactive = 0; // Se ajustará según la lógica de markup
				newStats.average = "0"; // Se calculará según los datos reales
				break;

			default:
				newStats.active = dataToAnalyze.length;
				newStats.inactive = 0;
		}

		setStats(newStats);
	};

	const calculateStats = () => {
		calculateStatsForData(data);
	};

	const handleDateRangeChange = (event, picker) => {
		const newDateRange = {
			startDate: picker.startDate,
			endDate: picker.endDate,
		};

		setDateRange(newDateRange);

		// Si es markup o copyTrading, recargar datos con nuevas fechas usando las fechas del picker directamente
		if (selectedTable === "markup" || selectedTable === "copyTrading") {
			loadTableDataWithDates(newDateRange);
		} else {
			filterData(picker.startDate, picker.endDate, filterStatus, filterSymbol, filterAccountType, filterPositionStatus, filterMasterName);
		}
	};

	// Función para obtener símbolos únicos
	const getUniqueSymbols = () => {
		if (selectedTable !== "positionsHistory") return [];
		const symbols = [...new Set(originalData.map((item) => item.symbol).filter(Boolean))];
		return symbols.sort();
	};

	// Función para obtener estados únicos de posiciones
	const getUniquePositionStatuses = () => {
		if (selectedTable !== "positionsHistory") return [];
		const statuses = [...new Set(originalData.map((item) => item.status).filter(Boolean))];
		return statuses.sort();
	};

	// Función para obtener valores únicos de masterName (Master Traders)
	const getUniqueMasterNames = () => {
		if (selectedTable !== "copyTrading") return [];
		const masterNameValues = [...new Set(originalData.map((item) => item.masterName).filter(Boolean))];
		return masterNameValues.sort();
	};

	const filterData = (
		startDate,
		endDate,
		status,
		symbol = filterSymbol,
		accountType = filterAccountType,
		positionStatus = filterPositionStatus,
		masterName = filterMasterName
	) => {
		let filtered = [...originalData];

		// Filtrar por fecha
		filtered = filtered.filter((item) => {
			const itemDate = moment(item.createdAt || item.created || item.date || item.timestamp || item.entryDate);
			return itemDate.isBetween(startDate, endDate, "day", "[]");
		});

		// Filtrar por estado general
		if (status !== "all") {
			filtered = filtered.filter((item) => {
				const itemStatus = item.status || item.estado;
				if (status === "active") {
					return itemStatus === "active" || itemStatus === "1" || itemStatus === true;
				} else {
					return itemStatus === "inactive" || itemStatus === "0" || itemStatus === false;
				}
			});
		}

		// Filtrar por símbolo (solo para positionsHistory)
		if (selectedTable === "positionsHistory" && symbol !== "all") {
			filtered = filtered.filter((item) => item.symbol === symbol);
		}

		// Filtrar por tipo de cuenta (para positionsHistory y copyTrading)
		if ((selectedTable === "positionsHistory" || selectedTable === "copyTrading") && accountType !== "all") {
			filtered = filtered.filter((item) => {
				let accountField = "";
				if (selectedTable === "positionsHistory") {
					accountField = item.accountID || "";
				} else if (selectedTable === "copyTrading") {
					accountField = item.copyFrom || "";
				}
				
				const isReal = accountField.toString().startsWith("CR");
				if (accountType === "real") {
					return isReal;
				} else if (accountType === "demo") {
					return !isReal;
				}
				return true;
			});
		}

		// Filtrar por estado de posición (solo para positionsHistory)
		if (selectedTable === "positionsHistory" && positionStatus !== "all") {
			filtered = filtered.filter((item) => item.status === positionStatus);
		}

		// Filtrar por Master Trader / masterName (solo para copyTrading)
		if (selectedTable === "copyTrading" && masterName !== "all") {
			filtered = filtered.filter((item) => item.masterName === masterName);
		}

		setData(filtered);
		setCurrentPage(0);

		// Recalcular estadísticas con los datos filtrados
		calculateStatsForData(filtered);
	};

	const handleExportData = () => {
		// Preparar datos para exportación
		const csvContent = [Object.keys(data[0] || {}).join(","), ...data.map((row) => Object.values(row).join(","))].join("\n");

		// Crear blob y descargar
		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);
		link.setAttribute("href", url);
		link.setAttribute("download", `reporte_${selectedTable}_${moment().format("YYYY-MM-DD")}.csv`);
		link.style.visibility = "hidden";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);

		alerts.success("Exportado", "Los datos han sido exportados correctamente");
	};

	const handleItemsPerPageChange = (event) => {
		setItemsPerPage(Number(event.target.value));
		setCurrentPage(0);
	};

	const filteredData = data.filter((item) =>
		Object.entries(item).some(([key, val]) => String(val).toLowerCase().includes(searchTerm.toLowerCase()))
	);

	const offset = currentPage * itemsPerPage;
	const currentPageData = filteredData.slice(offset, offset + itemsPerPage);
	const pageCount = Math.ceil(filteredData.length / itemsPerPage);

	const handlePageClick = (event) => {
		setCurrentPage(event.selected);
	};

	return (
		<div>
			<div className="d-flex align-items-center mb-3">
				<div>
					<h1 className="page-header mb-0">📊 Centro de Reportes</h1>
				</div>
				<div className="ms-auto">
					<button onClick={handleExportData} className="btn btn-outline-theme me-2" disabled={data.length === 0}>
						<i className="fa fa-download fa-fw me-1"></i> Exportar CSV
					</button>
					<button onClick={loadTableData} className="btn btn-outline-primary">
						<i className="fa fa-refresh fa-fw me-1"></i> Actualizar
					</button>
				</div>
			</div>

			{/* Estadísticas Rápidas */}
			<div className="row mb-3">
				<div className="col-xl-3 col-md-6 mb-3">
					<div className="card h-100">
						<div className="card-body d-flex align-items-center">
							<div className="me-3">
								<Chart
									options={{
										chart: { type: "radialBar", width: 60, height: 60, sparkline: { enabled: true } },
										plotOptions: {
											radialBar: { hollow: { size: "50%" }, dataLabels: { show: false }, track: { background: "#e0e0e0" } },
										},
										colors: ["#6c757d"],
									}}
									series={[100]}
									type="radialBar"
									width={60}
									height={60}
								/>
							</div>
							<div className="flex-fill">
								<h6 className="mb-1" style={{ fontSize: "0.875rem", fontWeight: "normal" }}>
									Total Registros
								</h6>
								<h3 className="mb-0">{stats.total}</h3>
							</div>
						</div>
					</div>
				</div>
				<div className="col-xl-3 col-md-6 mb-3">
					<div className="card h-100">
						<div className="card-body d-flex align-items-center">
							<div className="me-3">
								<Chart
									options={{
										chart: { type: "donut", width: 60, height: 60, sparkline: { enabled: true } },
										dataLabels: { enabled: false },
										legend: { show: false },
										colors: ["#28a745", "#e9ecef"],
									}}
									series={[Number(stats.active) || 0, Math.max(0, (Number(stats.total) || 0) - (Number(stats.active) || 0))]}
									type="donut"
									width={60}
									height={60}
								/>
							</div>
							<div className="flex-fill">
								<h6 className="mb-1" style={{ fontSize: "0.875rem", fontWeight: "normal" }}>
									{selectedTable === "accounts"
										? "Reales"
										: selectedTable === "positionsHistory"
										? "Ganadores"
										: selectedTable === "users"
										? "Activos"
										: selectedTable === "tickets"
										? "Abiertos"
										: selectedTable === "copyTrading"
										? "Copy Masters"
										: selectedTable === "markup"
										? "Positivos"
										: "Activos"}
								</h6>
								<h3 className="mb-0">{stats.active}</h3>
							</div>
						</div>
					</div>
				</div>
				<div className="col-xl-3 col-md-6 mb-3">
					<div className="card h-100">
						<div className="card-body d-flex align-items-center">
							<div className="me-3">
								<Chart
									options={{
										chart: { type: "donut", width: 60, height: 60, sparkline: { enabled: true } },
										dataLabels: { enabled: false },
										legend: { show: false },
										colors: ["#ffc107", "#e9ecef"],
									}}
									series={[Number(stats.inactive) || 0, Math.max(0, (Number(stats.total) || 0) - (Number(stats.inactive) || 0))]}
									type="donut"
									width={60}
									height={60}
								/>
							</div>
							<div className="flex-fill">
								<h6 className="mb-1" style={{ fontSize: "0.875rem", fontWeight: "normal" }}>
									{selectedTable === "accounts"
										? "Demo"
										: selectedTable === "positionsHistory"
										? "Perdedores"
										: selectedTable === "users"
										? "Eliminados"
										: selectedTable === "tickets"
										? "Cerrados"
										: selectedTable === "copyTrading"
										? "Total Followers"
										: selectedTable === "markup"
										? "Negativos"
										: "Inactivos"}
								</h6>
								<h3 className="mb-0">{stats.inactive}</h3>
							</div>
						</div>
					</div>
				</div>
				<div className="col-xl-3 col-md-6 mb-3">
					<div className="card h-100">
						<div className="card-body d-flex align-items-center">
							<div className="me-3">
								<Chart
									options={{
										chart: { type: "line", width: 60, height: 60, sparkline: { enabled: true } },
										stroke: { width: 2, curve: "smooth" },
										colors: ["#17a2b8"],
									}}
									series={[
										{
											data: [
												Math.floor(Math.random() * 50) + 25,
												Math.floor(Math.random() * 50) + 25,
												Math.floor(Math.random() * 50) + 25,
												Math.floor(Math.random() * 50) + 25,
												Math.floor(Math.random() * 50) + 25,
												Math.floor(Math.random() * 50) + 25,
												Math.floor(Math.random() * 50) + 25,
											],
										},
									]}
									type="line"
									width={60}
									height={60}
								/>
							</div>
							<div className="flex-fill">
								<h6 className="mb-1" style={{ fontSize: "0.875rem", fontWeight: "normal" }}>
									{selectedTable === "accounts"
										? "Balance"
										: selectedTable === "positionsHistory"
										? "Suma Total"
										: selectedTable === "users"
										? "% Admins"
										: selectedTable === "tickets"
										? "T. Resolución"
										: selectedTable === "copyTrading"
										? "Total Monto"
										: selectedTable === "markup"
										? "Markup Promedio"
										: "Promedio"}
								</h6>
								<h3 className="mb-0">
									{selectedTable === "positionsHistory" ? "$" : ""}
									{stats.average}
								</h3>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Filtros y Controles */}
			<Card>
				<div className="card-header">
					<h4 className="card-title">Filtros y Selección de Datos</h4>
				</div>
				<div className="card-body">
					<div className="row">
						<div className="col-md-3 mb-3">
							<label className="form-label">Seleccionar Tabla de Datos</label>
							<select className="form-select" value={selectedTable} onChange={(e) => setSelectedTable(e.target.value)}>
								{availableTables.map((table) => (
									<option key={table.value} value={table.value}>
										{table.label}
									</option>
								))}
							</select>
						</div>
						<div className="col-md-3 mb-3">
							<label className="form-label">Rango de Fechas</label>
							<DateRangePicker startDate={dateRange.startDate} endDate={dateRange.endDate} onApply={handleDateRangeChange}>
								<div className="input-group">
									<input
										type="text"
										className="form-control"
										value={`${dateRange.startDate.format("DD/MM/YYYY")} - ${dateRange.endDate.format("DD/MM/YYYY")}`}
										readOnly
									/>
									<span className="input-group-text">
										<i className="fa fa-calendar"></i>
									</span>
								</div>
							</DateRangePicker>
						</div>
						<div className="col-md-3 mb-3">
							<label className="form-label">Estado General</label>
							<select
								className="form-select"
								value={filterStatus}
								onChange={(e) => {
									setFilterStatus(e.target.value);
									filterData(dateRange.startDate, dateRange.endDate, e.target.value, filterSymbol, filterAccountType, filterPositionStatus, filterMasterName);
								}}>
								<option value="all">Todos</option>
								<option value="active">Activos</option>
								<option value="inactive">Inactivos</option>
							</select>
						</div>
						<div className="col-md-3 mb-3">
							<label className="form-label">Tipo de Cuenta</label>
							<select
								className="form-select"
								value={filterAccountType}
								onChange={(e) => {
									setFilterAccountType(e.target.value);
									filterData(
										dateRange.startDate,
										dateRange.endDate,
										filterStatus,
										filterSymbol,
										e.target.value,
										filterPositionStatus,
										filterMasterName
									);
								}}
								disabled={selectedTable !== "positionsHistory" && selectedTable !== "copyTrading"}>
								<option value="all">Todas</option>
								<option value="real">Real (CR)</option>
								<option value="demo">Demo</option>
							</select>
						</div>
					</div>
					{selectedTable === "positionsHistory" && (
						<div className="row">
							<div className="col-md-6 mb-3">
								<label className="form-label">Símbolo</label>
								<select
									className="form-select"
									value={filterSymbol}
									onChange={(e) => {
										setFilterSymbol(e.target.value);
										filterData(
											dateRange.startDate,
											dateRange.endDate,
											filterStatus,
											e.target.value,
											filterAccountType,
											filterPositionStatus,
											filterMasterName
										);
									}}>
									<option value="all">Todos los símbolos</option>
									{getUniqueSymbols().map((symbol) => (
										<option key={symbol} value={symbol}>
											{symbol}
										</option>
									))}
								</select>
							</div>
							<div className="col-md-6 mb-3">
								<label className="form-label">Estado de Posición</label>
								<select
									className="form-select"
									value={filterPositionStatus}
									onChange={(e) => {
										setFilterPositionStatus(e.target.value);
										filterData(
											dateRange.startDate,
											dateRange.endDate,
											filterStatus,
											filterSymbol,
											filterAccountType,
											e.target.value,
											filterMasterName
										);
									}}>
									<option value="all">Todos los estados</option>
									{getUniquePositionStatuses().map((status) => (
										<option key={status} value={status}>
											{status}
										</option>
									))}
								</select>
							</div>
						</div>
					)}
					{selectedTable === "copyTrading" && (
						<div className="row">
							<div className="col-md-6 mb-3">
								<label className="form-label">Master Trader</label>
								<select
									className="form-select"
									value={filterMasterName}
									onChange={(e) => {
										setFilterMasterName(e.target.value);
										filterData(
											dateRange.startDate,
											dateRange.endDate,
											filterStatus,
											filterSymbol,
											filterAccountType,
											filterPositionStatus,
											e.target.value
										);
									}}>
									<option value="all">Todos los Master Traders</option>
									{getUniqueMasterNames().map((masterName) => (
										<option key={masterName} value={masterName}>
											{masterName}
										</option>
									))}
								</select>
							</div>
						</div>
					)}
				</div>
			</Card>

			{/* Gráficos */}
			{chartData && !isLoading && chartData.series && chartData.options && (
				<Card className="mb-3">
					<div className="card-header">
						<h4 className="card-title">Visualización de Datos</h4>
					</div>
					<div className="card-body">
						<Chart
							key={`chart-${selectedTable}-${data.length}`}
							options={chartData.options}
							series={chartData.series}
							type={chartData.options?.chart?.type || "bar"}
							height={350}
							width="100%"
						/>
					</div>
				</Card>
			)}

			{/* Tabla de Datos */}
			<Card>
				<div className="card-header">
					<h4 className="card-title">Datos Detallados - {availableTables.find((t) => t.value === selectedTable)?.label}</h4>
				</div>
				<div className="card-body">
					<div className="row mb-3">
						<div className="col-md-6">
							<div className="input-group">
								<span className="input-group-text">
									<i className="fa fa-search"></i>
								</span>
								<input
									type="text"
									className="form-control"
									placeholder="Buscar en los datos..."
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
								/>
							</div>
						</div>
						<div className="col-md-6">
							<div className="d-flex justify-content-end align-items-center">
								<label className="me-2">Filas por página:</label>
								<select className="form-select w-auto" value={itemsPerPage} onChange={handleItemsPerPageChange}>
									<option value={10}>10</option>
									<option value={20}>20</option>
									<option value={50}>50</option>
									<option value={100}>100</option>
								</select>
							</div>
						</div>
					</div>

					{isLoading && (
						<div className="text-center py-5">
							<div className="spinner-border text-primary" role="status">
								<span className="visually-hidden">Cargando...</span>
							</div>
							<p className="mt-2">Cargando datos de {selectedTable}...</p>
						</div>
					)}

					{!isLoading && data.length === 0 && (
						<div className="text-center py-5">
							<i className="fa fa-inbox fa-3x text-muted mb-3"></i>
							<h4>No se encontraron datos</h4>
							<p className="text-muted">Intenta ajustar los filtros o seleccionar otra tabla</p>
						</div>
					)}

					{!isLoading && currentPageData.length > 0 && (
						<>
							<div className="table-responsive">
								<table className="table table-hover">
									<thead>
										<tr>
											{Object.keys(currentPageData[0] || {}).map((key, i) => (
												<th key={i} className="align-middle">
													{key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1")}
												</th>
											))}
										</tr>
									</thead>
									<tbody>
										{currentPageData.map((item, index) => (
											<tr key={index}>
												{Object.entries(item).map(([key, value], i) => (
													<td key={i} className="align-middle">
														{key === "status" || key === "estado" ? (
															value === "1" || value === "active" || value === true ? (
																<span className="badge bg-success">Activo</span>
															) : (
																<span className="badge bg-danger">Inactivo</span>
															)
														) : key.toLowerCase().includes("date") || key.toLowerCase().includes("created") ? (
															moment(value).format("DD/MM/YYYY HH:mm")
														) : key.toLowerCase().includes("password") ? (
															"********"
														) : typeof value === "boolean" ? (
															value ? (
																"✅"
															) : (
																"❌"
															)
														) : selectedTable === "copyTrading" && key === "copyFrom" ? (
															<>
																<span
																	style={{
																		backgroundColor: String(value).startsWith("CR")
																			? "rgba(0, 255, 0, 0.2)"
																			: "rgba(255, 165, 0, 0.2)",
																		color: String(value).startsWith("CR") ? "#00ff00" : "#ff8c00",
																		padding: "4px 12px",
																		borderRadius: "12px",
																		fontWeight: "600",
																		fontSize: "12px",
																		display: "inline-block",
																		border: String(value).startsWith("CR")
																			? "1px solid rgba(0, 255, 0, 0.3)"
																			: "1px solid rgba(255, 165, 0, 0.3)",
																		marginRight: "8px",
																	}}>
																	{String(value).startsWith("CR") ? "Real" : "Demo"}
																</span>
																<span style={{ fontSize: "11px", color: "#888" }}>{value}</span>
															</>
														) : (
															value || "-"
														)}
													</td>
												))}
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="d-flex justify-content-between align-items-center mt-3">
								<div>
									Mostrando {offset + 1} a {Math.min(offset + itemsPerPage, filteredData.length)} de {filteredData.length} registros
								</div>
								<ReactPaginate
									previousLabel={<i className="fa fa-chevron-left"></i>}
									nextLabel={<i className="fa fa-chevron-right"></i>}
									pageCount={pageCount}
									onPageChange={handlePageClick}
									containerClassName={"pagination mb-0"}
									previousLinkClassName={"page-link"}
									nextLinkClassName={"page-link"}
									disabledClassName={"disabled"}
									activeClassName={"active"}
									pageClassName={"page-item"}
									pageLinkClassName={"page-link"}
									breakClassName={"page-item"}
									breakLinkClassName={"page-link"}
								/>
							</div>
						</>
					)}
				</div>
			</Card>
		</div>
	);
}

export default Reports;
